import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Plus, X } from 'lucide-react';

/** Same normalisation the server applies: collapse whitespace and trim. */
export const cleanClassification = (s: string) => s.replace(/\s+/g, ' ').trim();

type Row =
  | { kind: 'option'; value: string }
  | { kind: 'create'; value: string }
  | { kind: 'clear' };

/**
 * Inline searchable picker for a classification value (type, vertical, niche…).
 * Picking an existing value is the default; a new value needs an explicit
 * "Create" row, which is hidden whenever the text already matches an existing
 * value ignoring case and spacing — so typing "home & garden" offers
 * "Home & Garden" instead of a near-duplicate. The server snaps variants too.
 * Only admins can create (`canCreate`); the server enforces the same rule.
 */
export default function ClassificationCombobox({
  value,
  options,
  canCreate,
  saving,
  onCommit,
  onCancel,
}: {
  value: string | null;
  options: string[];
  /** Whether a value not in `options` may be created (admins only). */
  canCreate: boolean;
  saving?: boolean;
  /** Called with the chosen value, or null to clear it. */
  onCommit: (value: string | null) => void;
  onCancel: () => void;
}) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const rows: Row[] = useMemo(() => {
    const q = cleanClassification(query).toLowerCase();
    const matches = options
      .filter((o) => !q || o.toLowerCase().includes(q))
      // Prefix matches first, then alphabetical.
      .sort((a, b) => {
        const pa = a.toLowerCase().startsWith(q) ? 0 : 1;
        const pb = b.toLowerCase().startsWith(q) ? 0 : 1;
        return pa - pb || a.localeCompare(b);
      })
      .map((o): Row => ({ kind: 'option', value: o }));
    const exists = !!q && options.some((o) => cleanClassification(o).toLowerCase() === q);
    const out: Row[] = [...matches];
    if (q && !exists && canCreate) out.push({ kind: 'create', value: cleanClassification(query) });
    if (value && !q) out.push({ kind: 'clear' });
    return out;
  }, [query, options, value, canCreate]);

  useEffect(() => { setActive(0); }, [query]);

  // Keep the highlighted row in view while arrowing through a long list.
  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  // Click outside cancels, like Escape.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) onCancel();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onCancel]);

  const pick = (row: Row | undefined) => {
    if (!row || saving) return;
    if (row.kind === 'clear') onCommit(null);
    else onCommit(row.value);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, rows.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); pick(rows[active]); }
    else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
  };

  return (
    <div ref={boxRef} className="relative flex-1">
      <div className="flex items-center gap-1.5">
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={value ? `${value} — type to search or change` : 'Type to search…'}
          className="input-sm flex-1"
          disabled={saving}
          role="combobox"
          aria-expanded="true"
          aria-autocomplete="list"
        />
        <button onClick={onCancel} className="btn-ghost text-slate-400 hover:text-slate-600" title="Cancel">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div
        ref={listRef}
        role="listbox"
        className="absolute left-0 right-8 mt-1 z-20 max-h-64 overflow-y-auto bg-surface border border-slate-200 rounded-md shadow-lg py-1"
      >
        {rows.length === 0 && (
          <div className="px-3 py-2 text-xs text-slate-400">
            {canCreate
              ? 'No values yet — type to create one'
              : query.trim()
                ? 'No matching value. Only admins can add new values.'
                : 'No values yet. Only admins can add new values.'}
          </div>
        )}
        {rows.map((row, idx) => {
          const isActive = idx === active;
          const base = `w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left ${isActive ? 'bg-brand-50' : ''}`;
          if (row.kind === 'create') {
            return (
              <button key="__create" data-idx={idx} type="button" role="option" aria-selected={isActive}
                onMouseEnter={() => setActive(idx)} onClick={() => pick(row)}
                className={`${base} text-brand-700 border-t border-slate-100`}>
                <Plus className="h-3.5 w-3.5 shrink-0" /> Create “{row.value}”
              </button>
            );
          }
          if (row.kind === 'clear') {
            return (
              <button key="__clear" data-idx={idx} type="button" role="option" aria-selected={isActive}
                onMouseEnter={() => setActive(idx)} onClick={() => pick(row)}
                className={`${base} text-slate-500 border-t border-slate-100`}>
                <X className="h-3.5 w-3.5 shrink-0" /> Clear value
              </button>
            );
          }
          const current = row.value === value;
          return (
            <button key={row.value} data-idx={idx} type="button" role="option" aria-selected={isActive}
              onMouseEnter={() => setActive(idx)} onClick={() => pick(row)}
              className={`${base} ${current ? 'font-medium text-brand-700' : 'text-slate-700'}`}>
              <span className="flex-1 truncate">{row.value}</span>
              {current && <Check className="h-3.5 w-3.5 text-brand-600 shrink-0" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
